import { simAction, simAdvance, simPress, type AthleteSim } from './sim/athlete.js';

export const INPUT_WINDOW = 0.25;
export type TimedInput = { t: number } & (
  { kind: 'press'; key: 0 | 1 } | { kind: 'action'; down: boolean }
);
const simTime = (s: AthleteSim) => (s.kind === 'run' ? s.state.t : s.attemptStart + s.state.t);

/** A bounded history for correcting input that arrives after the last simulation tick. */
export class InputReplay {
  private serial = 0;
  private inputs: (TimedInput & { serial: number })[] = [];
  private checkpoints: { t: number; serial: number; sim: AthleteSim }[] = [];

  reset(sim: AthleteSim): void {
    this.serial = 0;
    this.inputs = [];
    this.checkpoints = [];
    this.capture(sim);
  }

  capture(sim: AthleteSim): void {
    const t = simTime(sim);
    const last = this.checkpoints.at(-1);
    if (last && last.t === t && last.serial === this.serial) return;
    this.checkpoints.push({ t, serial: this.serial, sim: structuredClone(sim) });
    // Retain the last checkpoint before the window as well as all newer ones.
    while (this.checkpoints.length > 1 && this.checkpoints[1]!.t < t - INPUT_WINDOW)
      this.checkpoints.shift();
    const serial = this.checkpoints[0]!.serial;
    this.inputs = this.inputs.filter((i) => i.serial > serial);
  }

  apply(sim: AthleteSim, items: TimedInput[]): AthleteSim {
    if (!items.length) return sim;
    if (!this.checkpoints.length) this.reset(sim);
    let index = this.checkpoints.findLastIndex((c) => c.t <= items[0]!.t + 1e-9);
    if (index < 0) index = 0;
    const checkpoint = this.checkpoints[index]!;
    this.checkpoints.length = index + 1;
    this.inputs.push(...items.map((i) => ({ ...i, serial: ++this.serial })));
    const replay = this.inputs
      .filter((i) => i.serial > checkpoint.serial)
      .sort((a, b) => a.t - b.t || a.serial - b.serial);
    const restored = structuredClone(checkpoint.sim);
    for (const input of replay) {
      const t = Math.max(checkpoint.t, input.t);
      simAdvance(restored, t);
      if (input.kind === 'press') simPress(restored, input.key, t);
      else simAction(restored, input.down, t);
    }
    simAdvance(restored, Math.max(simTime(sim), replay.at(-1)?.t ?? simTime(sim)));
    return restored;
  }
}
